import { AnimatePresence, m } from "framer-motion";
import { RefObject, useEffect, useState } from "react";
import { Icon } from "./Icon";
import { EASE } from "./Motion";

/**
 * Close enough to the end to count as arrived. Without a little slack the
 * button flickers back on for the last pixel of an inertial scroll, and
 * sub-pixel layout means `scrollHeight` and `scrollTop + clientHeight` never
 * quite agree at the bottom anyway.
 */
const AT_BOTTOM = 24;

/**
 * The "there's more below" button, for a list whose end is hidden behind
 * something pinned over it.
 *
 * It only appears while there is something to scroll to, and takes itself
 * away on arrival — so on a short list that fits, it never shows at all and
 * costs the reader nothing.
 */
export function ScrollForMore({
  containerRef,
  label = "Vis flere",
}: {
  /** The scrolling element to watch and to scroll. */
  containerRef: RefObject<HTMLElement>;
  label?: string;
}) {
  const [more, setMore] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      setMore(el.scrollHeight - el.scrollTop - el.clientHeight > AT_BOTTOM);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });

    // The box changing size is not the only thing that moves the bottom: the
    // display font arrives after first paint and reflows every card under it,
    // which is exactly the moment a list goes from fitting to not. A
    // ResizeObserver on the container alone won't see that — its own border
    // box doesn't change — so the children are watched too.
    const observer = new ResizeObserver(update);
    observer.observe(el);
    for (const child of Array.from(el.children)) observer.observe(child);

    return () => {
      el.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [containerRef]);

  const scrollToEnd = () => {
    const el = containerRef.current;
    if (!el) return;
    // MotionConfig's reducedMotion doesn't reach scrollTo, so this asks for
    // itself. Anyone who's turned motion down gets the jump, not the glide.
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo({ top: el.scrollHeight, behavior: reduced ? "auto" : "smooth" });
  };

  return (
    <AnimatePresence>
      {more && (
        <m.button
          key="scroll-for-more"
          type="button"
          onClick={scrollToEnd}
          aria-label={label}
          className="absolute bottom-3 left-1/2 z-10 h-9 pl-3 pr-3.5 rounded-full bg-card border border-line-soft shadow-[0_2px_10px_rgba(0,0,0,0.10)] text-ink2 font-mono text-[9.5px] tracking-[1.3px] uppercase flex items-center gap-1.5"
          // The horizontal centring lives in framer's transform rather than a
          // `-translate-x-1/2` class: framer writes the whole `transform`
          // property, so a Tailwind translate on the same element is
          // overwritten the moment it animates.
          style={{ x: "-50%" }}
          initial={{ opacity: 0, y: 8, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.9 }}
          transition={{ duration: 0.2, ease: EASE }}
        >
          <Icon name="chevron-down" size={11} />
          {label}
        </m.button>
      )}
    </AnimatePresence>
  );
}
